import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LockIcon from '@mui/icons-material/Lock'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Stack,
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import { useContext, useEffect, useRef, useState } from 'react'
import useCloseExpedient from '../hooks/useCloseExpedient'
import useFetchCamposExpediente from '../hooks/useFetchCamposExpediente'
import useFetchDetalleExpediente from '../hooks/useFetchDetalleExpediente'
import useFetchFicherosExpediente from '../hooks/useFetchFicherosExpediente'
import { asociateFiles } from '../libs/FilesHelper'
import { puedeActualizar } from '../libs/PermisosHelper'
import { ExpedientesContext } from '../providers/ExpedientesProvider'
import ListItemDocument from './ListItemDocument'
import FileComponent from './fields/FileComponent'

function ExpedienteView({ expediente, onBack, onUpload }) {
  const {
    active: { current: tipo_expediente },
    expedientes_usuario,
    mostrar,
    campos_expediente,
    idProceso,
    setFicherosSubidos,
    setIdProceso,
    setCamposExpediente,
    setCamposOrigen,
    ficherosSubidos,
    setExpedienteActual,
  } = useContext(ExpedientesContext)

  const {
    response: responseCampos,
    refetch: refetchCampos,
    loading: loadingCampos,
  } = useFetchCamposExpediente({
    expediente: tipo_expediente,
  })

  const { response, error, loading } = useFetchDetalleExpediente({
    tipo_expediente: tipo_expediente?.tipo_expediente,
    numero_expediente: expediente.numero_expediente,
    programa: tipo_expediente?.programa,
    id_pers: tipo_expediente?.id_pers,
    plantilla: tipo_expediente?.plantilla,
  })

  const {
    response: response_ficheros,
    error: error_ficheros,
    loading: loading_ficheros,
    refetch: refetchFicheros,
  } = useFetchFicherosExpediente({
    tipo_expediente: tipo_expediente?.tipo_expediente,
    numero_expediente: expediente.numero_expediente,
  })

  const {
    cerrarExpediente,
    response: close_response,
    loading: loading_close,
  } = useCloseExpedient(tipo_expediente?.tipo_expediente, expediente.numero_expediente)

  const divRef = useRef(null)

  const [loadingFiles, setLoadingFiles] = useState(false)

  useEffect(() => {
    setFicherosSubidos([])
    setIdProceso(null)

    if (!campos_expediente && !loadingCampos) refetchCampos()
  }, [])

  useEffect(() => {
    setCamposExpediente(responseCampos?.expediente?.campos || [])
    setCamposOrigen(responseCampos?.expediente?.campos || [])
  }, [responseCampos])

  useEffect(() => {
    if (ficherosSubidos && ficherosSubidos.length > 0) {
      divRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [ficherosSubidos])

  useEffect(() => {
    if (close_response && close_response.expediente.status === '1000') {
      // El expediente se cerró correctamente
      setExpedienteActual(null)
      expedientes_usuario.refetchExpedientes(mostrar)
      onBack()
    }
  }, [close_response])

  const getLabelStatus = () => {
    switch (expediente.status) {
      case '0300':
        return 'Abierto'
      case '0500':
        return 'En espera de expedientes hijos'
      case '1000':
        return 'Cerrado'
      default:
        return 'Desconocido'
    }
  }

  const getColorStatus = () => {
    switch (expediente.status) {
      case '0300':
        return 'success'
      case '0500':
        return 'warning'
      case '1000':
        return 'error'
      default:
        return 'primary'
    }
  }

  const handleActualizarDocumentos = async () => {
    setLoadingFiles(true)

    await asociateFiles(tipo_expediente?.tipo_expediente, expediente.numero_expediente, idProceso)

    setLoadingFiles(false)
    setFicherosSubidos([])
    setIdProceso(null)
    refetchFicheros()
  }

  const handleCloseExpedient = async () => {
    cerrarExpediente(tipo_expediente.tipo_expediente, expediente.numero_expediente)
  }

  return (
    <Box key="detalle-expediente" sx={{ flexGrow: 1 }}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingFiles || loading_close}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container sx={{ width: '100%' }}>
        <Grid item xs={6} justifyContent={'flex-start'} width={'50%'}>
          <Button startIcon={<ArrowBackIcon />} variant="contained" onClick={onBack} sx={{ mb: 2 }}>
            Volver
          </Button>
        </Grid>
        {!loading && (
          <Grid item xs={6} display={'flex'} justifyContent={'flex-end'} width={'50%'}>
            <Button
              startIcon={<LockIcon />}
              variant="contained"
              color="error"
              onClick={handleCloseExpedient}
              sx={{ mb: 2 }}
            >
              Cerrar expediente
            </Button>
          </Grid>
        )}
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, mb: 4 }}>
          <Skeleton variant="rounded" width={'100%'} height={50} />
        </Box>
      ) : (
        <Chip
          label={getLabelStatus()}
          color={getColorStatus()}
          sx={{
            p: '25px',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            mt: 2,
            mb: 2,
            fontSize: 'x-large',
          }}
        />
      )}

      <Grid container spacing={2}>
        <Grid
          key="datos"
          size={{
            xs: 12,
            sm: 12,
            md:
              expediente.tiene_ficheros === 'S' ||
              response_ficheros?.expediente?.length > 0 ||
              loading_ficheros
                ? 8
                : 12,
          }}
        >
          <Card>
            <CardHeader title={'Nº expediente ' + expediente.numero_expediente} />
            <CardContent>
              <List sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {loading
                  ? [...Array(5).keys()].map((index) => {
                      return (
                        <>
                          <ListItem id={index} alignItems="flex-start">
                            <ListItemText primary={<Skeleton />} secondary={<Skeleton />} />
                          </ListItem>
                          {index < 4 ? <Divider variant="middle" component="li" /> : <></>}
                        </>
                      )
                    })
                  : response?.campos.map((c) => {
                      if (c.oculto === 'S' || c.tipo_input === 'file') return null

                      let valor = null
                      const secondary = null

                      switch (c.tipo_input) {
                        case 'text':
                        case 'textarea':
                          valor = c.valor
                          break
                        case 'number':
                          valor = Number(c.valor)
                          break
                        case 'date':
                          valor = c.valor || ''
                          break
                        case 'lv':
                          valor = c.d_valor ? c.d_valor : c.valor
                          break
                        default:
                          break
                      }

                      return (
                        <>
                          <ListItem
                            key={c.columna}
                            sx={{
                              width: { lg: c.ancho ? c.ancho + '%' : '100%', sm: '100%' },
                            }}
                          >
                            <ListItemText
                              primary={valor}
                              secondary={c.literal}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column-reverse',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                pb: 1,
                                mb: 1,
                              }}
                            />
                          </ListItem>
                        </>
                      )
                    })}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid key="documentos" size={{ xs: 12, sm: 12, md: 4 }}>
          {(expediente.tiene_ficheros === 'S' ||
            response_ficheros?.expediente?.length > 0 ||
            loading_ficheros) && (
            <Stack spacing={2}>
              <Card>
                <CardHeader title="Documentos del expediente" />
                <CardContent>
                  <List>
                    {loading_ficheros ? (
                      <>
                        {[...Array(3).keys()].map((_, idx) => (
                          <ListItem key={idx}>
                            <ListItemAvatar sx={{ color: 'gray' }}>
                              <Skeleton variant="circular" width={40} height={40} />
                            </ListItemAvatar>
                            <Box>
                              <Skeleton variant="text" width={100} />
                              <Skeleton variant="rounded" width={150} />
                              <Skeleton variant="text" width={100} />
                            </Box>
                          </ListItem>
                        ))}
                      </>
                    ) : (
                      response_ficheros?.expediente?.map((f) => {
                        return (
                          <>
                            <ListItemDocument documento={f} />
                          </>
                        )
                      })
                    )}
                  </List>
                </CardContent>
              </Card>
            </Stack>
          )}
        </Grid>
      </Grid>

      {loadingCampos ? (
        <>
          {[...Array(1).keys()].map((_, idx) => (
            <Box key={idx} sx={{ mt: 4, mb: 2 }}>
              <Skeleton key={idx} variant="rounded" width="15%" height={15} sx={{ mb: 2 }} />
              <Skeleton key={idx} variant="rounded" width="100%" height={55} sx={{ mb: 1 }} />
              <Skeleton key={idx} variant="rectangular" width="100%" height={110} />
            </Box>
          ))}
        </>
      ) : (
        !loading &&
        !loadingCampos &&
        responseCampos?.expediente?.campos?.some((campo) => campo.tipo_input === 'file') &&
        tipo_expediente?.tipos_documentos?.length > 0 &&
        puedeActualizar(tipo_expediente) && (
          <Box mt={3}>
            {responseCampos?.expediente?.campos
              ?.filter((campo) => campo.tipo_input === 'file')
              .map((campo) => (
                <Box key={campo.columna} sx={{ mb: 2 }} ref={divRef}>
                  <FileComponent
                    key={campo.columna}
                    columna={campo.columna}
                    documentTypes={tipo_expediente.tipos_documentos}
                  />
                  {ficherosSubidos && ficherosSubidos.length > 0 && (
                    <Button
                      variant="contained"
                      color="error"
                      size="large"
                      sx={{ marginTop: 2 }}
                      onClick={() => handleActualizarDocumentos()}
                    >
                      ACTUALIZAR DOCUMENTOS
                    </Button>
                  )}
                </Box>
              ))}
          </Box>
        )
      )}
    </Box>
  )
}

export default ExpedienteView
